"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.COMPLEXITY_BEARING_MIN_RATING = exports.AUTHORED_LINE_AMBIGUOUS_MAX = exports.AUTHORED_LINE_EXEMPT_MAX = exports.AUTHORED_LINE_CATEGORIES = exports.FRAMEWORK_LABELS = void 0;
exports.formatFramework = formatFramework;
/**
 * Human-readable display labels for {@link FrameworkType} values (spec #631, AC-3).
 *
 * Only frameworks whose internal code literal differs from the name a human should
 * see need an entry here. Currently that is `pdlc` → `SDD-PDLC`,
 * `cai-discovery-skill` → `CAI Discovery Skill`, and `spec-kit` → `GitHub Spec Kit`
 * — the internal union values stay `'pdlc'`/`'cai-discovery-skill'`/`'spec-kit'`
 * (AC-4/AC-4.2: no type-gate/tag-matching change), but every UI/report render site
 * must show the product's actual name. Any {@link FrameworkType} without an entry
 * falls through to its own value via {@link formatFramework} (identity).
 *
 * This is a shared cross-workspace constant: the same producer/consumer mapping is
 * consumed by the dashboard's badges, chart legends, and filter dropdowns, so it is
 * defined once here in `@sdd-maturity/shared-types` rather than re-declared per site.
 */
exports.FRAMEWORK_LABELS = {
    pdlc: 'SDD-PDLC',
    'cai-discovery-skill': 'CAI Discovery Skill',
    'spec-kit': 'GitHub Spec Kit',
};
/**
 * Resolve the human-readable display label for a framework value (spec #631, AC-3).
 *
 * Returns the mapped label from {@link FRAMEWORK_LABELS} when one exists, otherwise
 * the value itself (identity passthrough). Accepts a plain `string` because several
 * render sites carry the framework as a raw string (e.g. sentinel values like
 * `'stale'`/`'inaccessible'` that are not part of the {@link FrameworkType} union) —
 * any unmapped input passes through unchanged, so display-only labeling never alters
 * filtering or type-gate logic.
 */
function formatFramework(framework) {
    return exports.FRAMEWORK_LABELS[framework] ?? framework;
}
/**
 * Categories counted toward "authored" line volume (path-audit-line-counts
 * TASK-1, AC-2.1) — the seam constant `#1109`'s zero-authored-volume
 * exemption boundary imports verbatim rather than re-literalling (shared
 * cross-lambda constants live once in `shared-types` — constitution,
 * `docs/constitution.md:468-476`). `ci` is deliberately excluded: a later
 * "cosmetic" inclusion would silently reopen the 21%->8%
 * zero-authored-population collapse the issue measured.
 */
exports.AUTHORED_LINE_CATEGORIES = new Set([
    'source',
    'iac',
    'helm',
]);
/**
 * Authored-line-volume band boundaries `#1109` consumes verbatim for its
 * zero-authored-volume exemption (path-audit-line-counts TASK-1, AC-2.1).
 * `authoredLines <= AUTHORED_LINE_EXEMPT_MAX` is the zero-authored-volume
 * exemption band; `authoredLines <= AUTHORED_LINE_AMBIGUOUS_MAX` (and above
 * the exempt band) is the ambiguous band `#1109` treats as inconclusive.
 */
exports.AUTHORED_LINE_EXEMPT_MAX = 0;
exports.AUTHORED_LINE_AMBIGUOUS_MAX = 25;
/**
 * Minimum LLM-assigned complexity rating (1-10 scale) a rated commit must
 * meet to count toward `ComplexityAdmissionResult.complexityBearingCount`
 * (complexity-admission-gate, AC-2.2). Exported so the admission decision in
 * `repo-scanner` imports the threshold rather than re-literalling a bare `4`
 * at the call site.
 */
exports.COMPLEXITY_BEARING_MIN_RATING = 4;
//# sourceMappingURL=assessment.js.map